import React, { useCallback, useEffect } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DevTool } from '@hookform/devtools';
import { LoaderFunction, NavLink, useFetcher, useLoaderData, useNavigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import MainSection from '../../../../components/Core/MainSection/MainSection';
import Card from '../../../../components/Core/Card/Card';
import TextFieldControlled, {
  TextAreaFieldControlled,
} from '../../../../components/Form/ControlledFields/TextFieldControlled';
import Label from '../../../../components/Core/Label/Label';
import { useMainTitle } from '../../../../components/Layout/MainTitleContext';
import DropzoneFileInput from '../../../../components/Core/DropzoneFileInput/DropzoneFileInput';
import ConnectForm from '../../../../components/Form/ConnectForm';
import ButtonWithIcon from '../../../../components/Core/Button/ButtonWithIcon';
import useDisplayError from '../../../../hook/useDisplayError';
import { TOAST_SEVERITY } from '../../../../components/Core/Toast/Toast';
import { useSnackbar } from '../../../../context/SnackbarContext';
import store from '../../../../internal/store';
import { velocareTypesQuery } from '../Bike/NewBike.page';
import SelectField from '../../../../components/Core/SelectField';
import { Option } from '../../../../hook/useBuildForm';
import styles from './NewBike.module.scss';

export const newParkLoader: (queryClient: QueryClient) => LoaderFunction = (queryClient) => async () => {
  const collId = store.getState().authState.collectivity;

  const velocareTypesResponse = await queryClient.ensureQueryData({
    ...velocareTypesQuery({ id_coll: collId }),
  });

  return { collId, velocareTypesResponse };
};

export function ParkForm() {
  const { t } = useTranslation(['parameters/products/newPark', 'form']);
  const fieldsT = useCallback((key: string) => t(`fields.${key}`), [t]);

  const { velocareTypesResponse } = useLoaderData() as {
    velocareTypesResponse?: AxiosResponse<[]>;
  };

  return (
    <ConnectForm>
      {({ register }) => (
        <>
          <div className={styles.FirstSection}>
            <div className={styles.Left}>
              <fieldset>
                <Label required={true}>{fieldsT('name.label')}</Label>
                <TextFieldControlled
                  placeholder={fieldsT('name.placeholder')}
                  {...register('name', { required: true })}
                />
              </fieldset>
              <fieldset className={styles.DescriptionField}>
                <Label required={true}>{fieldsT('description.label')}</Label>
                <TextAreaFieldControlled
                  placeholder={fieldsT('description.placeholder')}
                  {...register('description', { required: true })}
                />
              </fieldset>
            </div>
            <div className={styles.Right}>
              <fieldset>
                <Controller
                  name={'img'}
                  defaultValue={null}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Label>{t('fields.img.label')}</Label>
                      <DropzoneFileInput
                        returnFile
                        className={styles.ImgInput}
                        //eslint-disable-next-line
                        onChange={(file: any) => {
                          const reader = new FileReader();
                          reader.onload = function (e) {
                            if (e.target && e.target.result && typeof e.target.result === 'string') {
                              const src = e.target.result;
                              // onChange(dataURLtoFile(src, file.path));
                              onChange({ src });
                            }
                          };

                          reader.readAsDataURL(file as File);
                        }}
                        defaultValue={value}
                      />
                    </>
                  )}
                />
              </fieldset>
            </div>
            <div style={{ display: 'flex' }}>
              {velocareTypesResponse && !_.isNil(velocareTypesResponse?.data) && velocareTypesResponse.data.length > 0 && (
                <div style={{ minWidth: 300 }}>
                  <Label>{fieldsT('velocareType.label')}</Label>
                  <Controller
                    name={'velocareType'}
                    render={({ fieldState: { error }, field: { onChange, value } }) => (
                      <SelectField
                        placeholder={fieldsT('velocareType.placeholder')}
                        error={error}
                        onChange={(newValue) => {
                          onChange(newValue);
                        }}
                        //eslint-disable-next-line
                        options={velocareTypesResponse?.data as any}
                        value={velocareTypesResponse?.data.find((option: Option) => option.value === value) || null}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </ConnectForm>
  );
}

function NewParkPage() {
  const fetcher = useFetcher();
  const { t } = useTranslation(['parameters/products/newPark', 'form']);
  const methods = useForm({ mode: 'onChange' });
  const navigate = useNavigate();
  useDisplayError(fetcher.data);
  useMainTitle(t('title'));
  const snackbar = useSnackbar();

  const onSubmit = (data: FieldValues) => {
    fetcher.submit({ action: 'new', ...data }, { method: 'post', encType: 'application/json' });
  };

  useEffect(() => {
    if (fetcher.data && fetcher.data.status === 200) {
      snackbar?.setAlert({
        message: t('successMessage'),
        severity: TOAST_SEVERITY.success,
      });
      navigate('/parametres/articles/consigne');
    }
  }, [fetcher.data, t]);

  return (
    <MainSection className={'reset'}>
      <Card
        content={
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <p>{t('form:all_required')}</p>
              <ParkForm />
              <div className={'row mt-4 justify-between items-center'}>
                <ButtonWithIcon
                  as={NavLink}
                  to={'/parametres/articles/consigne'}
                  icon={'CrossWithoutCircle'}
                  color={'secondary'}
                  type={'button'}>
                  {t('common:cancel')}
                </ButtonWithIcon>
                <ButtonWithIcon icon={'CheckWithoutCircle'} type={'submit'}>
                  {t('submitCta')}
                </ButtonWithIcon>
              </div>
            </form>

            <DevTool control={methods.control} />
          </FormProvider>
        }
      />
    </MainSection>
  );
}

export default NewParkPage;
